import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Breadcrumbs from '@components/layout/Breadcrumbs';
import { useContactModal } from '@contexts/ContactModalContext';
import { CONTACT_TOPICS } from '@components/contact/TopicConfig';

/**
 * ContactPage — Topic selector grid (fallback for non-contextual contact)
 * Route: /contact
 *
 * Displays all 11 contact topics as cards.
 * Deep-linking: /contact?topic=request-availability&product=...&sku=...&qty=...
 *
 * For contextual triggers (from PDP, orders, etc.) pages call
 * useContactModal().openContact(topicId, context) directly.
 */
export default function ContactPage() {
  const { openContact } = useContactModal();
  const [searchParams, setSearchParams] = useSearchParams();

  const urlTopic = searchParams.get('topic');
  const urlContext = {
    productName: searchParams.get('product') || '',
    sku: searchParams.get('sku') || '',
    quantity: searchParams.get('qty') || '',
    orderNumber: searchParams.get('order') || '',
  };

  // Auto-open modal if deep-linked with ?topic=...
  useEffect(() => {
    if (urlTopic) {
      openContact(urlTopic, urlContext);
      // Clear the URL params so modal doesn't re-open on close
      setSearchParams({}, { replace: true });
    }
  }, [urlTopic]);

  const TOPIC_ICONS = {
    'others': 'chat',
    'product-info': 'info',
    'become-customer': 'person_add',
    'request-order': 'shopping_cart',
    'request-availability': 'inventory',
    'product-sellability': 'sell',
    'missing-rig': 'construction',
    'missing-drill': 'handyman',
    'order-issue': 'report_problem',
    'return-request': 'assignment_return',
    'request-alternate': 'swap_horiz',
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Contact us' }]} />
      <h1 className="font-sandvik-headline font-bold text-2xl text-carbon-black mb-2">
        Contact us
      </h1>
      <p className="text-sm text-n5 mb-8">
        Choose a topic below and we will help you get in touch with the right team.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {CONTACT_TOPICS.map(topic => (
          <button key={topic.id} onClick={() => openContact(topic.id, {})}
            className="text-left p-4 border border-n2 hover:border-electric-blue hover:shadow-sm transition-all group">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-light/30 rounded-full flex items-center justify-center group-hover:bg-electric-blue/10 transition-colors">
                <span className="material-symbols-outlined text-base text-electric-blue">
                  {TOPIC_ICONS[topic.id] || 'chat'}
                </span>
              </div>
              <div>
                <h3 className="text-sm font-medium text-carbon-black group-hover:text-electric-blue transition-colors">
                  {topic.label}
                </h3>
                <p className="text-xs text-n5 mt-1 line-clamp-2">{topic.description}</p>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
